import { toHalfFloat } from 'three/src/extras/DataUtils.js';
import { cubeFace, panoFaces, type PanoFaces } from '../pano-faces.js';

/**
 * A world's sky made ready off the page (ADR-032, ADR-036's pano worker):
 * from the HDRI's float image, the background's six cube faces -- the sky
 * baked to 8-bit and sampled as a 360° photo's are -- and the environment
 * light's 1024 × 512 half-float copy. About a quarter of a second of a
 * desktop's time, a second of a Quest's, that would otherwise be one frame.
 *
 * Pure maths and no three.js beyond its half-float conversion (from its
 * sources, so the worker does not carry the rest), for the pano worker
 * (pano-work.ts); look/sky.ts puts the parts on the GPU.
 */

/** An equirectangular float image, RGBA, rows top first, as three's HDRLoader reads it. */
export type FloatImage = {
  readonly width: number;
  readonly height: number;
  readonly data: Float32Array;
};

/** A half-float RGBA image, rows top first: the light's copy. */
export type HalfImage = {
  readonly width: number;
  readonly height: number;
  readonly data: Uint16Array;
};

/** What the worker is asked for a world's sky. */
export type SkyAsk = {
  readonly image: FloatImage;
  /** The world's `sky.exposure`, baked into the faces. */
  readonly exposure: number;
  /** The GPU's `maxCubemapSize`. */
  readonly maxFace: number;
  /** A sky nobody sees (indoors, under water) only lights: no faces. */
  readonly seen: boolean;
};

/** A world's sky, ready to go up: its faces (when seen) and its light's copy. */
export type SkyParts = {
  readonly faces?: PanoFaces;
  readonly light: HalfImage;
  /** What it cost, ms: baking to 8-bit, sampling the faces, halving for the light. */
  readonly ms: { readonly bake: number; readonly faces: number; readonly light: number };
};

/** The widest the light's copy is: a 256 px PMREM cube (look/sky.ts). */
export const SKY_LIGHT_WIDTH = 1024;

/** three.js's ACES filmic tone mapping (tonemapping_pars_fragment.glsl), then the sRGB curve. */
export function displayed(
  r: number,
  g: number,
  b: number,
  exposure: number,
): [number, number, number] {
  const s = exposure / 0.6;
  const x = r * s;
  const y = g * s;
  const z = b * s;
  const i0 = 0.59719 * x + 0.35458 * y + 0.04823 * z;
  const i1 = 0.076 * x + 0.90834 * y + 0.01566 * z;
  const i2 = 0.0284 * x + 0.13383 * y + 0.83777 * z;
  const fit = (v: number): number =>
    (v * (v + 0.0245786) - 0.000090537) / (v * (0.983729 * v + 0.432951) + 0.238081);
  const f0 = fit(i0);
  const f1 = fit(i1);
  const f2 = fit(i2);
  const srgb = (v: number): number => {
    const c = Math.min(1, Math.max(0, v));
    return c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055;
  };
  return [
    srgb(1.60475 * f0 - 0.53108 * f1 - 0.07367 * f2),
    srgb(-0.10208 * f0 + 1.10813 * f1 - 0.00605 * f2),
    srgb(-0.00327 * f0 - 0.07276 * f1 + 1.07602 * f2),
  ];
}

/** The sky as it will be seen: 8-bit sRGB, the rows as they came. */
export function bakeSky(image: FloatImage, exposure: number): Uint8Array {
  const { width, height, data } = image;
  const out = new Uint8Array(width * height * 4);
  for (let i = 0; i < width * height; i++) {
    const [r, g, b] = displayed(data[i * 4]!, data[i * 4 + 1]!, data[i * 4 + 2]!, exposure);
    out[i * 4] = Math.round(r * 255);
    out[i * 4 + 1] = Math.round(g * 255);
    out[i * 4 + 2] = Math.round(b * 255);
    out[i * 4 + 3] = 255;
  }
  return out;
}

/** Each 2 × 2 block averaged: half as wide and as tall. */
export function halve(image: FloatImage): FloatImage {
  const width = image.width >> 1;
  const height = image.height >> 1;
  const data = new Float32Array(width * height * 4);
  const at = (x: number, y: number, c: number): number =>
    image.data[(y * image.width + x) * 4 + c]!;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      for (let c = 0; c < 4; c++) {
        data[(y * width + x) * 4 + c] =
          (at(2 * x, 2 * y, c) +
            at(2 * x + 1, 2 * y, c) +
            at(2 * x, 2 * y + 1, c) +
            at(2 * x + 1, 2 * y + 1, c)) /
          4;
      }
    }
  }
  return { width, height, data };
}

/** The light's copy: halved to at most `maxWidth`, in half floats (linearly filterable everywhere). */
export function lightHalf(image: FloatImage, maxWidth = SKY_LIGHT_WIDTH): HalfImage {
  let small = image;
  while (small.width > maxWidth) small = halve(small);
  const data = new Uint16Array(small.data.length);
  for (let i = 0; i < small.data.length; i++) data[i] = toHalfFloat(small.data[i]!);
  return { width: small.width, height: small.height, data };
}

/**
 * The sky's parts: its faces a third of its width on a side (pano-faces.ts),
 * at most `maxFace`, when it is seen; and its light's copy. Throws on an
 * image shorter than its size says.
 */
export function skyParts(ask: SkyAsk): SkyParts {
  const { image, exposure, maxFace, seen } = ask;
  const { width, height } = image;
  if (image.data.length < width * height * 4) {
    throw new Error(`sky ${width} × ${height} has ${image.data.length} values`);
  }
  let faces: PanoFaces | undefined;
  const started = performance.now();
  let baked = started;
  let sampled = started;
  if (seen) {
    const pixels = bakeSky(image, exposure);
    baked = performance.now();
    const size = Math.min(cubeFace(width), maxFace);
    const data = panoFaces(pixels, width, height, size);
    sampled = performance.now();
    faces = {
      size,
      data,
      width,
      height,
      ms: { decode: baked - started, reproject: sampled - baked },
    };
  }
  const light = lightHalf(image);
  return {
    ...(faces ? { faces } : {}),
    light,
    ms: { bake: baked - started, faces: sampled - baked, light: performance.now() - sampled },
  };
}
